import { useState, useEffect } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import Spinner from "../../spinner/Spinner";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../../api/ApiServices";
import { toast } from "react-toastify";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import AxiosApiCall from "../../../api/AxiosApiCall";
import DataTable from "react-data-table-component";
import CustomStyles from "../../../components/utils/CustomStyles";

const GetIncentiveStat = () => {
  const [branchDetails, setBranchDetails] = useState({
    summary: {},
    branch_breakdown: [],
    period: {},
  });
  const [filterParams, setFilterParams] = useState({
    start_date: "",
    end_date: "",
  });

  const [branch_id, setBranchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  useEffect(() => {
    if (id) {
      setBranchId(id);
    }
  }, [id]);

  //axios api encryption call
  const axios = AxiosApiCall();

  const fetchBranchDetails = async () => {
    setLoading(true);
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) throw new Error("No access token found.");

      const response = await axios.get(ApiServices.getBranchOpeStatistic, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: { branch_id },
        withCredentials: true,
      });

      const { status, message, data } = response.data;
      if (status === 200) {
        setBranchDetails(data);
        toast.success(message, { toastId: "incentive-stat-fetch-success" });
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (branch_id) {
      fetchBranchDetails();
    }
  }, [branch_id]);

  // YYYY-MM-DD
  // const formatDate = (dateStr) => {
  //   const date = new Date(dateStr);
  //   return date.toISOString().slice(0, 10);
  // };


  // Format dates properly to YYYY-MM-DD
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // for data filter
  const handleApplyFilter = async () => {
    if (!filterParams.start_date || !filterParams.end_date) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (new Date(filterParams.end_date) < new Date(filterParams.start_date)) {
      toast.error("End date cannot be before start date");
      return;
    }

    setLoading(true);
    try {
      const authData = JSON.parse(localStorage.getItem("auth"));
      const token = authData?.token;
      if (!token) throw new Error("No access token found.");


      const formData = {
        branch_id,
        start_date: formatDate(filterParams.start_date),
        end_date: formatDate(filterParams.end_date),
      };

      // console.log("form data to submit", formData);

      const response = await axios.get(ApiServices.getBranchOpeStatistic, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: formData,
        withCredentials: true,
      });

      const { status, message, data } = response.data;
      if (status === 200) {
        setBranchDetails(data);
        toast.success(message, { toastId: "incentive-stat-fetch-success" });
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    } finally {
      setLoading(false);
    }
  };


  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const filteredItems = branchDetails.branch_breakdown.filter((item) =>
    Object.keys(item).some((key) =>
      item[key]?.toString().toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const breakdownColumns = [
    {
      name: "Branch",
      selector: (row) => row.branch_name.toUpperCase() || "NOT AVAILABLE",
      sortable: true,
    },
    {
      name: "Total Incentives",
      selector: (row) => CurrencyFormatter(row.total_incentives || 0),
      sortable: true,
    },
    {
      name: "Paid Incentives",
      selector: (row) => CurrencyFormatter(row.paid_incentives || 0),
      sortable: true,
    },
    {
      name: "Pending Incentives",
      selector: (row) => CurrencyFormatter(row.pending_incentives || 0),
      sortable: true,
    },
  ];

  return (
    <MainLayouts>
      <div className="relative sm:rounded-lg py-6 w-full">
        <section className="w-full pr-[3%] pt-3 my-3 bg-gray-100">
          <nav className="bg-gray-200 py-0 px-4 rounded-lg">
            <ol className="list-none p-0 inline-flex mb-2">
              <li className="flex items-center">
                <p className="text-darkGreen3 pr-2">Operations Branch</p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-darkGreen3 pr-2">Incentives</p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-orange1">Statistic</p>
              </li>
            </ol>
          </nav>
        </section>

        {loading ? (
          <Spinner loading={loading} />
        ) : (
          <section className="text-gray-600 body-font">
            <div className="container">
              <div className="flex justify-end mb-3">
                <Link
                  to="/operations/branch/list"
                  className="w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                  <span className="flex justify-center sm:justify-start mx-2">
                    <ArrowLeftSquare className="mr-2" />
                    Back
                  </span>
                </Link>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 p-2">
                  <div className="flex items-center border-dash border p-1 rounded-lg border-l-4">
                    <section className="body-font w-full">
                      <div className="container px-2">
                        <div className="w-full flex flex-wrap">
                          <div className="w-full px-4">
                            <h1 className="text-sm title-font mt-2">
                              <span className="text-orange1 text-uppercase">Branch Name</span>:{" "}
                              {branchDetails.branch_breakdown[0]?.branch_name?.toUpperCase() || "Not Available"}
                            </h1>

                            <div className="mt-6 my-4">
                              <h3 className="text-dash text-lg mb-2">Period</h3>
                              <div className="bg-gray-50 p-4 rounded-lg shadow">
                                <p>
                                  <strong>Start Date:</strong>{" "}
                                  {new Date(branchDetails.period.start_date).toLocaleDateString()}
                                </p>
                                <p>
                                  <strong>End Date:</strong>{" "}
                                  {new Date(branchDetails.period.end_date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            <div className="flex border-t border-gray-200 py-2">
                              <span className="text-gray-500">Total Incentives</span>
                              <span className="ml-auto text-dash">
                                {CurrencyFormatter(branchDetails.summary.total_incentives || 0)}
                              </span>
                            </div>
                            <div className="flex border-t border-gray-200 py-2">
                              <span className="text-gray-500">Total Paid</span>
                              <span className="ml-auto text-dash">
                                {CurrencyFormatter(branchDetails.summary.total_paid || 0)}
                              </span>
                            </div>
                            <div className="flex border-t border-gray-200 py-2">
                              <span className="text-gray-500">Total Pending</span>
                              <span className="ml-auto text-dash">
                                {CurrencyFormatter(branchDetails.summary.total_pending || 0)}
                              </span>
                            </div>
                            <div className="flex border-t border-gray-200 py-2">
                              <span className="text-gray-500">Average Per Branch</span>
                              <span className="ml-auto text-dash">
                                {CurrencyFormatter(branchDetails.summary.average_per_branch || 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12 p-2">
                  <section className="body-font w-full">
                    <div className="container px-2">
                      <div className="w-full flex flex-wrap">
                        <div className="flex flex-wrap w-full gap-4 mb-4 sm:items-end">
                          <div className="w-full sm:w-1/4">
                            <label className="block mb-1 text-sm text-gray-600">Start Date</label>
                            <input
                              type="date"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              value={filterParams.start_date}
                              onChange={(e) =>
                                setFilterParams({ ...filterParams, start_date: e.target.value })
                              }
                            />
                          </div>

                          <div className="w-full sm:w-1/4">
                            <label className="block mb-1 text-sm text-gray-600">End Date</label>
                            <input
                              type="date"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              value={filterParams.end_date}
                              onChange={(e) =>
                                setFilterParams({ ...filterParams, end_date: e.target.value })
                              }
                            />
                          </div>

                          <div className="w-full sm:w-1/4">
                            <label className="block mb-1 text-sm text-gray-600">Search</label>
                            <input
                              type="search"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              placeholder="Search branch breakdown..."
                              value={filterText}
                              onChange={handleFilter}
                            />
                          </div>

                          <div className="w-full sm:w-auto mt-2 sm:mt-6 flex gap-2">
                            <button
                              onClick={handleApplyFilter}
                              className="bg-darkGreen3 text-dGrey3 px-4 py-2 rounded-lg"
                            >
                              Filter
                            </button>

                            <button
                              onClick={() => {
                                setFilterParams({ start_date: "", end_date: "" });
                                fetchBranchDetails();
                              }}
                              className="bg-orange1 text-dGrey3 px-4 py-2 rounded-lg"
                            >
                              Reset
                            </button>
                          </div>


                        </div>


                        <div className="mt-6 w-full">
                          <h3 className="text-dash text-lg mb-2">Branch Breakdown</h3>
                          <div className="bg-white p-4 rounded-lg shadow">
                            <DataTable
                              columns={breakdownColumns}
                              data={filteredItems}
                              pagination
                              highlightOnHover
                              striped
                              customStyles={CustomStyles}
                              noDataComponent={<div>No breakdown data found</div>}
                              paginationResetDefaultPage={resetPaginationToggle}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </MainLayouts>
  );
};

export default GetIncentiveStat;
